/**
 * The workspace's confirmations (docs/ux/workspace.md, "Destructive-action
 * safety"): Accept all and Reject all decide several of QA's suggestions in
 * one click; Complete QA finalises the report. Each names what happens, in
 * words, and carries an inline error when the write fails.
 */

import { useRef, useState } from 'react';
import { longDate } from '@/lib/format';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { pluralise } from '@/lib/format';

type ConfirmProps = { open: boolean; onOpenChange: (open: boolean) => void; onConfirm: () => Promise<boolean>; busyLabel: string; confirmLabel: string; cancelLabel: string; title: string; description: string; warning?: string | null };

function Confirm({ open, onOpenChange, onConfirm, busyLabel, confirmLabel, cancelLabel, title, description, warning }: ConfirmProps) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const inFlight = useRef(false);
  async function confirm() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setFailed(false);
    try {
      const ok = await onConfirm();
      if (ok) onOpenChange(false); else setFailed(true);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <AlertDialogContent className="max-w-[560px]">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {warning ? <p className="text-sm text-beam-warning">{warning}</p> : null}
        {failed ? <Alert variant="destructive" role="alert"><AlertDescription>That did not go through. Nothing changed. Try again.</AlertDescription></Alert> : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); void confirm(); }}>{busy ? busyLabel : confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export type BulkDecision = { sectionKey: string; sectionLabel: string; decision: 'accept' | 'reject'; count: number; withoutReason: number };

export function BulkDecisionDialog({ pending, onOpenChange, onConfirm }: { pending: BulkDecision | null; onOpenChange: (open: boolean) => void; onConfirm: (p: BulkDecision) => Promise<boolean> }) {
  const p = pending;
  const accept = p?.decision === 'accept';
  return (
    <Confirm
      open={p !== null}
      onOpenChange={onOpenChange}
      onConfirm={() => (p ? onConfirm(p) : Promise.resolve(false))}
      title={p ? `${accept ? 'Accept' : 'Reject'} all ${pluralise(p.count, 'suggestion')} in ${p.sectionLabel}?` : ''}
      description={p ? (accept
        ? `Each box in the section will read exactly as QA rewrote it. ${p.withoutReason === 0 ? 'Every suggestion carries a reason from QA.' : `${p.withoutReason} of the ${p.count} ${p.count === 1 ? 'suggestion has' : 'suggestions have'} no reason from QA.`} This is recorded against you.`
        : `The report's words stay as they are. QA's ${pluralise(p.count, 'suggestion')} move to the declined list without a reason; QA can ask about each one. This is recorded against you.`) : ''}
      cancelLabel="Keep deciding"
      confirmLabel={accept ? 'Accept all' : 'Reject all'}
      busyLabel={accept ? 'Accepting' : 'Rejecting'}
    />
  );
}

export function CompleteQaDialog({ open, onOpenChange, onConfirm, matterRef, unanswered, today }: { open: boolean; onOpenChange: (open: boolean) => void; onConfirm: () => Promise<boolean>; matterRef: string; unanswered: number; today: string }) {
  return (
    <Confirm
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={onConfirm}
      title={`Complete QA for ${matterRef}?`}
      description={`The report moves to 9 QA Complete and is finalised today, ${longDate(today)}. Nothing in the workspace can be edited after this; the preview and export remain. The release pack is built from the finalised text.`}
      warning={unanswered > 0 ? `${pluralise(unanswered, 'box is', 'boxes are')} still empty. The final export will refuse until they are answered or the empty boxes are intended.` : null}
      cancelLabel="Keep reviewing"
      confirmLabel="Complete QA"
      busyLabel="Completing"
    />
  );
}
